import { createContext, useContext, useEffect, useMemo, useState } from "react";

const dict = {
  en: {
    nav: {
      home: "Home",
      philosophy: "Philosophy",
      approach: "Approach",
      sectors: "Sectors",
      portfolio: "Investments",
      news: "News",
    },
    cta: {
      learnMore: "Learn more",
      explorePhilosophy: "Explore the philosophy",
      seePortfolio: "See investments",
      readMore: "Read more",
      viewAll: "View all",
    },
    hero: {
      eyebrow: "FAMILY EQUITY · SINCE 2006",
      title: "Owners, not investors. Generations, not quarters.",
      subtitle:
        "We build companies that create impact — across clean technologies, circular economy and natural resources.",
      stat1: "since 2006",
      stat2: "€100M+ allocated",
      stat3: "Europe · US · Asia · Middle East",
    },
    philosophy: {
      eyebrow: "OUR PHILOSOPHY",
      title: "Family Equity",
      lead: "We unite the strategic patience of a family business with the disciplined value-creation approach of private equity.",
      p1: "Unlike traditional private equity, we operate without fixed investment horizons. Our capital is permanent — there is no predefined exit or expiration date.",
      p2: "This enables us to make decisions that prioritize lasting value over immediate gains, aligning fully with the companies we own.",
      pillarsTitle: "What this means in practice",
      pillars: [
        { t: "Owner-managed", d: "Decisions are made by the people whose capital is at stake." },
        { t: "Owner-financed", d: "We invest our own permanent capital, not third-party fund money." },
        { t: "No exit pressure", d: "We hold for as long as we add value — often across decades." },
        { t: "Aligned interests", d: "Long-term thinking creates better businesses and better partners." },
      ],
    },
    approach: {
      eyebrow: "OUR APPROACH",
      title: "Active partners. Sustainable value.",
      lead: "We support our portfolio companies in realizing their full potential — across the full lifecycle of the investment.",
      pillars: [
        {
          t: "Strategic & operational development",
          d: "Sustainable value creation through hands-on collaboration with management teams.",
        },
        {
          t: "Resilience & competitiveness",
          d: "Strengthening the long-term resilience of every business we own.",
        },
        {
          t: "Future-proofing",
          d: "Preparing companies for future challenges and market transitions.",
        },
        {
          t: "Reliable partnership",
          d: "Acting as a reliable partner over the full lifecycle of an investment.",
        },
      ],
    },
    sectors: {
      eyebrow: "INVESTMENT FOCUS",
      title: "A more sustainable, resource-efficient economy.",
      lead: "We invest in sectors that build the foundation of long-term industrial transformation.",
      list: [
        { key: "clean_tech", t: "Clean Technologies", d: "Energy storage, intelligent grids, electrification of industry." },
        { key: "biogas", t: "Biogas & Biomass", d: "Renewable energy from agricultural residues and organic streams." },
        { key: "recycling", t: "Recycling & Circular Economy", d: "Industrial-scale circularity for metals, polymers and materials." },
        { key: "natural_resources", t: "Natural Resources", d: "Sustainably sourced raw materials and natural-fiber production." },
      ],
      geoTitle: "Our geographies",
      geo: ["Europe", "United States", "Asia", "Middle East"],
    },
    portfolio: {
      eyebrow: "INVESTMENTS",
      title: "Companies built to endure.",
      lead: "A selection of businesses we have invested in and developed alongside.",
      empty: "Investments will appear here once published.",
    },
    track: {
      eyebrow: "TRACK RECORD",
      title: "Patient capital. Compounding outcomes.",
      lead: "Since 2006, Elephant Equity has allocated more than €100 million across industries and geographies. Our consistency reflects our belief in patient capital and responsible ownership.",
      kpi: [
        { k: "2006", l: "Founded" },
        { k: "€100M+", l: "Capital allocated" },
        { k: "3", l: "Continents" },
        { k: "0", l: "Fixed exit dates" },
      ],
    },
    news: {
      eyebrow: "NEWS & INSIGHTS",
      title: "Notes from a long-term owner.",
      lead: "Selected updates from our portfolio and our perspective on long-term ownership.",
      empty: "News will be published here.",
    },
    footer: {
      tagline: "Responsibility over generations.",
      address: "Elephant Equity",
      copyright: "© {year} Elephant Equity. All rights reserved.",
      links: "Quick links",
      imprint: "Imprint",
      privacy: "Privacy",
      legal: "Legal",
      admin: "Admin",
    },
    sectorLabels: {
      clean_tech: "Clean Tech",
      biogas: "Biogas & Biomass",
      recycling: "Recycling",
      natural_resources: "Natural Resources",
    },
  },
  de: {
    nav: {
      home: "Start",
      philosophy: "Philosophie",
      approach: "Ansatz",
      sectors: "Sektoren",
      portfolio: "Beteiligungen",
      news: "Aktuelles",
    },
    cta: {
      learnMore: "Mehr erfahren",
      explorePhilosophy: "Philosophie entdecken",
      seePortfolio: "Beteiligungen ansehen",
      readMore: "Weiterlesen",
      viewAll: "Alle ansehen",
    },
    hero: {
      eyebrow: "FAMILY EQUITY · SEIT 2006",
      title: "Eigentümer, keine Investoren. Generationen, keine Quartale.",
      subtitle:
        "Wir bauen Unternehmen, die Wirkung entfalten – in Cleantech, Kreislaufwirtschaft und natürlichen Ressourcen.",
      stat1: "seit 2006",
      stat2: "über 100 Mio. € investiert",
      stat3: "Europa · USA · Asien · Mittlerer Osten",
    },
    philosophy: {
      eyebrow: "UNSERE PHILOSOPHIE",
      title: "Family Equity",
      lead: "Wir verbinden die strategische Geduld eines Familienunternehmens mit der disziplinierten Wertschaffung von Private Equity.",
      p1: "Anders als klassische Private-Equity-Häuser arbeiten wir ohne feste Investitionshorizonte. Unser Kapital ist dauerhaft – es gibt kein vordefiniertes Exitdatum.",
      p2: "Dies ermöglicht Entscheidungen, die nachhaltigen Wert vor kurzfristigen Erträgen stellen – und schafft echte Interessenausrichtung.",
      pillarsTitle: "Was das in der Praxis bedeutet",
      pillars: [
        { t: "Inhabergeführt", d: "Entscheidungen treffen die Menschen, deren Kapital eingesetzt ist." },
        { t: "Eigenfinanziert", d: "Wir investieren eigenes, dauerhaftes Kapital – kein Fremdfondskapital." },
        { t: "Ohne Exitdruck", d: "Wir halten so lange, wie wir Wert beitragen – oft über Jahrzehnte." },
        { t: "Echte Ausrichtung", d: "Langfristdenken schafft bessere Unternehmen und bessere Partner." },
      ],
    },
    approach: {
      eyebrow: "UNSER ANSATZ",
      title: "Aktive Partner. Nachhaltiger Wert.",
      lead: "Wir unterstützen unsere Portfolio-Unternehmen, ihr volles Potenzial zu entfalten – über den gesamten Investitionslebenszyklus.",
      pillars: [
        { t: "Strategisch & operativ", d: "Nachhaltige Wertschaffung in enger Zusammenarbeit mit dem Management." },
        { t: "Resilienz & Wettbewerbsfähigkeit", d: "Stärkung der langfristigen Wettbewerbsfähigkeit unserer Unternehmen." },
        { t: "Zukunftssicherheit", d: "Vorbereitung auf zukünftige Herausforderungen und Marktwandel." },
        { t: "Verlässliche Partnerschaft", d: "Verlässlicher Partner über den gesamten Lebenszyklus." },
      ],
    },
    sectors: {
      eyebrow: "INVESTITIONSFOKUS",
      title: "Eine nachhaltigere, ressourceneffiziente Wirtschaft.",
      lead: "Wir investieren in Sektoren, die das Fundament der industriellen Transformation bilden.",
      list: [
        { key: "clean_tech", t: "Cleantech", d: "Energiespeicher, intelligente Netze, Elektrifizierung der Industrie." },
        { key: "biogas", t: "Biogas & Biomasse", d: "Erneuerbare Energie aus landwirtschaftlichen Reststoffen." },
        { key: "recycling", t: "Recycling & Kreislaufwirtschaft", d: "Industrielle Kreislauflösungen für Metalle, Polymere, Materialien." },
        { key: "natural_resources", t: "Natürliche Ressourcen", d: "Nachhaltig gewonnene Rohstoffe und Naturfaserproduktion." },
      ],
      geoTitle: "Unsere Regionen",
      geo: ["Europa", "USA", "Asien", "Mittlerer Osten"],
    },
    portfolio: {
      eyebrow: "BETEILIGUNGEN",
      title: "Unternehmen, die Bestand haben.",
      lead: "Eine Auswahl von Unternehmen, in die wir investiert haben und die wir gemeinsam mit ihrem Management weiterentwickeln.",
      empty: "Beteiligungen erscheinen hier sobald veröffentlicht.",
    },
    track: {
      eyebrow: "TRACK RECORD",
      title: "Geduldiges Kapital. Wirkung über Jahre.",
      lead: "Seit 2006 hat Elephant Equity über 100 Mio. € branchen- und länderübergreifend investiert. Konsequenz – Ausdruck unseres Glaubens an geduldiges Kapital und verantwortliches Eigentum.",
      kpi: [
        { k: "2006", l: "Gegründet" },
        { k: "100 Mio. €+", l: "Investiertes Kapital" },
        { k: "3", l: "Kontinente" },
        { k: "0", l: "Feste Exitdaten" },
      ],
    },
    news: {
      eyebrow: "AKTUELLES & EINBLICKE",
      title: "Notizen eines Langfrist-Eigentümers.",
      lead: "Ausgewählte Updates aus unserem Portfolio und unsere Sicht auf langfristiges Eigentum.",
      empty: "Hier werden Neuigkeiten veröffentlicht.",
    },
    footer: {
      tagline: "Verantwortung über Generationen.",
      address: "Elephant Equity",
      copyright: "© {year} Elephant Equity. Alle Rechte vorbehalten.",
      links: "Schnellzugriff",
      imprint: "Impressum",
      privacy: "Datenschutz",
      legal: "Rechtliches",
      admin: "Admin",
    },
    sectorLabels: {
      clean_tech: "Cleantech",
      biogas: "Biogas & Biomasse",
      recycling: "Recycling",
      natural_resources: "Natürliche Ressourcen",
    },
  },
};

const I18nCtx = createContext({ lang: "en", t: dict.en, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("ee_lang") || "en");
  useEffect(() => {
    localStorage.setItem("ee_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(() => ({ lang, t: dict[lang], setLang }), [lang]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}
